import { screenSmall, screenMedium } from '../utils/screen-sizes'
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme'
import { css } from '@emotion/core'

const { sysColorBackgroundSurface } = PAYPAL_THEME

// FlowModal
// =========
export const modalCss = css`
  position: fixed;
  z-index: 1049;
  width: 100%;
  background: #f4f6f9;
`

// Hide left and right overflow, so when page transition in from
// the side is not visible
export const modalContainerCss = css`
  overflow-x: hidden;

  // When sticky actions bar is enabled, for the extra padding so the
  // content can be scrolled above the sticky actions bar
  @media (max-width: ${screenSmall}) {
    // Hide sticky action bar by default and display only after
    // page animation has completed. Not hiding it, will cause
    // the action bar to be floating in the middle and moved to
    // to bottom after page slide ends.
    .step-enter {
      .papui_modal-actions--sticky {
        opacity: 0;
      }
    }

    .step-enter-done {
      .papui_modal-actions--sticky {
        opacity: 1;
        transition: opacity 0.2s ease-in;
      }
    }

    .papui_modal__content--sticky_actions {
      padding-bottom: 6rem;
    }

    .papui_modal__content {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }
`

// ModalContent
// ============

// Fixed modal header/footer spans across the screen width,
// which blocks the left/right grey overpanel background, setting the
// content width prevent that the overlap, note that the configs need
// to be updated when .ppvx_overpanel width is updated
export const modalContentCss = css`
  width: 100%;
  max-width: 44.875rem;
  height: 100%;
  background: ${sysColorBackgroundSurface};
  margin: 0 auto;
  align-self: start;

  @media (max-width: ${screenMedium}) {
    & {
      width: 100%;
      margin: 0 1.5rem;
      padding: 0 1.5rem;
    }
  }

  @media (max-width: ${screenSmall}) {
    & {
      width: 100%;
      padding: 0 0.75rem;
      margin: 0;
    }
  }
`

// ModalHeader
// ===========

export const headingTextCss = css`
  outline: none;
  margin: 0.75rem 0;
`

export const modalTitleCss = css`
  font-family: PayPalOpen-Regular, 'Helvetica Neue', Arial, sans-serif;
`

// To inherit flex settings from ppvx_modal-header so that
// back and close buttons could be left and right aligned
// modal title is center aligned
export const modalContentHeaderCss = css`
  display: inherit;
  justify-content: inherit;
  align-items: center;
`

export const hideNavBarCss = css`
  padding-top: 1rem !important;
`

export const showNavBarCss = css`
  h2#modal-header {
    margin-top: 0;
  }
`

// ModalActions
// ============

export const modalActionsCss = css`
  margin-top: 2.25rem;
  text-align: center;

  button {
    margin: 0 0.6rem 1rem 0;
  }

  button:last-child {
    margin-right: 0;
  }

  .buttonGroup {
    padding: 0 !important;
  }

  @media (min-width: ${screenSmall}) {
    button {
      // Override 100% width from default PPUI styling
      width: auto;
    }
  }

  @media (max-width: ${screenSmall}) {
    margin-top: auto;

    &.papui_modal-actions--sticky {
      position: fixed;
      width: 100%;
      left: 0;
      bottom: 0;
      z-index: 99;

      .papui_modal-actions__contents {
        padding: 1rem 1rem 2rem;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 26%);
      }

      .button {
        margin-bottom: 0.75rem;
      }
    }

    button {
      width: 100%;
      margin: 0 0.75rem 1rem 0;
    }

    .buttonGroup > button {
      margin: 0;
    }
  }
`

// PPVX Modal Overpanel Overrides
// TODO As of @paypalcorp/pp-react@6.4.0 overpanel has performance
// and sticky header issues on iOS, the overrides are temporary
// measures till issues are resolved in future version
// of pp-react
export const ppVxModalCss = css`
  background-color: #f4f6f9;

  // Disable scroll in overflow that comes ppvx overpanel
  // Scrolling is laggy inside a div, and browser bottom navigation
  // button is not hidden on scroll
  .ppvx_modal.ppvx_modal--overpanel {
    position: static;
    background: ${sysColorBackgroundSurface};

    // To prevent bottom sticky action bar from being
    // rendered behind navigation bar on iOS Safari
    transform: none;
    margin: 0 auto;

    // Hide border
    border: 0;

    // Set Max width to be consistent with old overpanel design
    max-width: 44.875rem;
  }

  // Sticky position does not always work on iOS webview
  .ppvx_modal-header {
    // Fixed position not supported in Safari
    // position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    background: none;
    z-index: 99;
  }

  // Add some default bottom padding to there's a bit of space
  // at the bottom of the page
  .ppvx_modal__contents {
    overflow: visible;
    padding: 0 8.5rem 4rem;
  }

  // Reduce header top spacing
  @media (max-width: ${screenSmall}) {
    #animation {
      height: 100%;
    }
    .papui_flow_modal--show_navbar {
      #modal-contents {
        position: relative;
      }
    }
  }

  // To match the latest design in ppvx library
  .ppvx_modal-header__action,
  .ppvx_modal-header__close {
    min-height: 0;
    padding: 0;
  }

  // Default mobile padding is too wide, i.e. 43px
  @media (max-width: ${screenSmall}) {
    .ppvx_modal.ppvx_modal--overpanel {
      width: 100% !important;

      // Set dynamic viewport height
      // to prevent iOS Safari floating address bar
      // from blocking the components at the bottom
      height: 100dvh;
    }

    .ppvx_modal .ppvx_modal__contents {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    .ppvx_container-fluid {
      padding-left: 0;
      padding-right: 0;
    }
  }

  @media (max-width: ${screenMedium}) {
    .ppvx_modal-header__action,
    .ppvx_modal-header__close {
      margin-left: 0;
      margin-right: 0;
    }
  }
`
